import { useQuery } from "@tanstack/react-query";
import {
  PageHeader,
  StatCard,
  Card,
  SectionHeader,
  Table,
  Badge,
  Spinner,
  QueryError,
} from "../components";
import { usePolling } from "../hooks/usePolling";
import { apiGet } from "../lib/api";

type LabType = "deployment" | "ux" | "bootstrap" | "travel" | "shopping";
type RecommendedAction =
  | "no_action"
  | "watch"
  | "investigate"
  | "fix_candidate"
  | "design_candidate";

interface LabFinding {
  findingId: string;
  labType: LabType;
  severity: "low" | "medium" | "high" | "critical";
  confidence: number;
  subsystem: string;
  probableArea: string;
  reproductionScenario: string;
  evidenceRefs: string[];
  likelyCause?: string;
  acceptanceCriteria: string[];
  recommendedAction: RecommendedAction;
  occurrenceCount: number;
  lastSeenAt: number;
}

interface MasterLabSummary {
  generatedAt: string;
  activeLabs: LabType[];
  blockedLabs: LabType[];
  topFindings: LabFinding[];
  recentImprovements: string[];
  nextImplementationTargets: string[];
  creditUsageSummary: {
    projectId: string;
    notes: string;
  };
}

const SEVERITY_BADGE_MAP: Record<LabFinding["severity"], "red" | "amber" | "blue" | "gray"> = {
  critical: "red",
  high: "red",
  medium: "amber",
  low: "gray",
};

const ACTION_BADGE_MAP: Record<RecommendedAction, "green" | "red" | "amber" | "blue" | "gray"> = {
  no_action: "green",
  watch: "gray",
  investigate: "amber",
  fix_candidate: "red",
  design_candidate: "blue",
};

const LAB_BADGE_MAP: Record<LabType, "blue" | "amber" | "green" | "secondary" | "gray"> = {
  deployment: "blue",
  ux: "amber",
  bootstrap: "green",
  travel: "secondary",
  shopping: "gray",
};

function formatTimestamp(value: number | string): string {
  const date = typeof value === "number" ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatConfidence(value: number): string {
  const normalized = Math.max(0, Math.min(1, value));
  return `${Math.round(normalized * 100)}%`;
}

function renderStringList(values: string[], emptyLabel: string) {
  if (values.length === 0) {
    return <span style={{ color: "var(--c-text-faint)" }}>{emptyLabel}</span>;
  }

  return (
    <ul className="space-y-2">
      {values.map((value) => (
        <li
          key={value}
          className="text-sm"
          style={{ color: "var(--c-text-secondary)", lineHeight: 1.5 }}
        >
          {value}
        </li>
      ))}
    </ul>
  );
}

export default function LabProgram() {
  const {
    data: summary,
    isLoading: loadingSummary,
    error: summaryError,
  } = useQuery<MasterLabSummary>({
    queryKey: ["labs", "summary"],
    queryFn: () => apiGet<MasterLabSummary>("/api/labs/summary"),
  });

  usePolling([["labs", "summary"]], 60_000);

  if (loadingSummary && !summary) {
    return (
      <div>
        <PageHeader title="Labs" subtitle="Certification and tagged deployment status" />
        <div className="flex items-center justify-center py-20">
          <Spinner size={24} />
        </div>
      </div>
    );
  }

  if (summaryError && !summary) {
    return (
      <div>
        <PageHeader title="Labs" subtitle="Certification and tagged deployment status" />
        <QueryError error={summaryError} queryKeys={[["labs", "summary"]]} />
      </div>
    );
  }

  const findings = summary?.topFindings ?? [];
  const escalatedFindings = findings.filter(
    (finding) => finding.recommendedAction === "fix_candidate",
  ).length;

  const findingColumns = [
    {
      key: "severity",
      header: "Severity",
      render: (row: LabFinding) => (
        <Badge color={SEVERITY_BADGE_MAP[row.severity]} dot>
          {row.severity}
        </Badge>
      ),
    },
    {
      key: "labType",
      header: "Lab",
      render: (row: LabFinding) => <Badge color={LAB_BADGE_MAP[row.labType]}>{row.labType}</Badge>,
    },
    {
      key: "subsystem",
      header: "Subsystem",
      render: (row: LabFinding) => (
        <div>
          <div className="font-medium text-sm" style={{ color: "var(--c-text)" }}>
            {row.subsystem}
          </div>
          <div className="text-xs mt-1" style={{ color: "var(--c-text-faint)" }}>
            {row.probableArea}
          </div>
        </div>
      ),
    },
    {
      key: "recommendedAction",
      header: "Action",
      render: (row: LabFinding) => (
        <Badge color={ACTION_BADGE_MAP[row.recommendedAction]}>
          {row.recommendedAction.replaceAll("_", " ")}
        </Badge>
      ),
    },
    {
      key: "occurrenceCount",
      header: "Occurrences",
      render: (row: LabFinding) => row.occurrenceCount.toLocaleString(),
    },
    {
      key: "confidence",
      header: "Confidence",
      render: (row: LabFinding) => formatConfidence(row.confidence),
    },
    {
      key: "lastSeenAt",
      header: "Last Seen",
      render: (row: LabFinding) => formatTimestamp(row.lastSeenAt),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Labs"
        subtitle="Track certification runs and tagged deployment cohorts in one place"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard title="Active Labs" value={String(summary?.activeLabs.length ?? 0)} />
        <StatCard title="Blocked Labs" value={String(summary?.blockedLabs.length ?? 0)} />
        <StatCard title="Top Findings" value={String(summary?.topFindings.length ?? 0)} />
        <StatCard
          title="Fix Candidates"
          value={String(escalatedFindings)}
          subtitle={`${summary?.nextImplementationTargets.length ?? 0} queued targets`}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-8">
        <Card className="xl:col-span-2">
          <SectionHeader
            title="Program Summary"
            subtitle={`Generated ${formatTimestamp(summary?.generatedAt ?? Date.now())}`}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            <div>
              <div
                className="text-[11px] uppercase tracking-widest font-medium mb-2"
                style={{ color: "var(--c-text-faint)" }}
              >
                Project
              </div>
              <div className="text-sm font-medium" style={{ color: "var(--c-text)" }}>
                {summary?.creditUsageSummary.projectId ?? "Unknown"}
              </div>
              <div className="text-sm mt-2" style={{ color: "var(--c-text-secondary)" }}>
                {summary?.creditUsageSummary.notes ?? "No lab notes available."}
              </div>
            </div>

            <div>
              <div
                className="text-[11px] uppercase tracking-widest font-medium mb-2"
                style={{ color: "var(--c-text-faint)" }}
              >
                Current Lab State
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                {(summary?.activeLabs ?? []).map((lab) => (
                  <Badge key={lab} color={LAB_BADGE_MAP[lab]}>
                    {lab}
                  </Badge>
                ))}
                {(summary?.activeLabs.length ?? 0) === 0 && (
                  <span style={{ color: "var(--c-text-faint)" }}>No active lab runs</span>
                )}
              </div>
              <div
                className="text-[11px] uppercase tracking-widest font-medium mb-2"
                style={{ color: "var(--c-text-faint)" }}
              >
                Blocked
              </div>
              <div className="flex flex-wrap gap-2">
                {(summary?.blockedLabs ?? []).map((lab) => (
                  <Badge key={lab} color="red">
                    {lab}
                  </Badge>
                ))}
                {(summary?.blockedLabs.length ?? 0) === 0 && (
                  <span style={{ color: "var(--c-text-faint)" }}>No blocked labs</span>
                )}
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <SectionHeader
            title="Recent Improvements"
            subtitle="Latest stable wins from unattended runs"
          />
          {renderStringList(
            summary?.recentImprovements ?? [],
            "No recent improvements captured yet.",
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        <Card>
          <SectionHeader
            title="Next Implementation Targets"
            subtitle="Highest-priority areas for the next development lane"
          />
          {renderStringList(
            summary?.nextImplementationTargets ?? [],
            "No implementation targets queued.",
          )}
        </Card>

        <Card>
          <SectionHeader title="Finding Quality" subtitle="How actionable the current backlog is" />
          <div className="space-y-4">
            <div>
              <div
                className="text-[11px] uppercase tracking-widest font-medium mb-1"
                style={{ color: "var(--c-text-faint)" }}
              >
                Escalated
              </div>
              <div className="text-2xl font-semibold" style={{ color: "var(--c-text)" }}>
                {escalatedFindings}
              </div>
              <div className="text-sm mt-1" style={{ color: "var(--c-text-secondary)" }}>
                Findings already ranked as immediate fix candidates.
              </div>
            </div>
            <div>
              <div
                className="text-[11px] uppercase tracking-widest font-medium mb-1"
                style={{ color: "var(--c-text-faint)" }}
              >
                Coverage
              </div>
              <div className="text-sm" style={{ color: "var(--c-text-secondary)" }}>
                {findings.length > 0
                  ? `Top ${findings.length} findings are visible below with severity, repetition, and confidence.`
                  : "No findings are available yet."}
              </div>
            </div>
          </div>
        </Card>
      </div>

      <SectionHeader
        title="Top Findings"
        subtitle="Repeated failures and degradations feeding the next implementation queue"
      />
      <Card padding={false}>
        <Table
          columns={findingColumns}
          data={findings}
          keyField="findingId"
          emptyMessage="No lab findings recorded yet."
        />
      </Card>
    </div>
  );
}
